import _ from "lodash";
import { TTerminal } from "../AppState/overagesFilterFormActions";
import { AppURLs, EAutoSearchFieldName, ETab } from "../constants";

export const toArray = (str: string, el: string = ",") => {
  if (!str) return str;
  return str.split(el);
};

export const toBool = (str: string) => {
  if (!str) return str;
  return str.toLowerCase().trim() === "true";
};

export const isEmptyResponse = (res: unknown): boolean => {
  if (res == null) return true; // handles null & undefined

  if (Array.isArray(res)) return res.length === 0;

  if (typeof res === "object") return Object.keys(res).length === 0;

  return false;
};

export const abbreviateNumber = (value: any) => {
  if (!value) return value;
  if (value < 1000) return value;
  value = Math.floor(value);
  let suffixes = ["", "k", "m", "b", "t"];
  let suffixNum = Math.floor(("" + value).length / 3);
  let shortValue: any = Number.parseFloat(
    (suffixNum === 0 ? value : value / Math.pow(1000, suffixNum)).toPrecision(
      2,
    ),
  );
  if (shortValue % 1 !== 0) {
    shortValue = shortValue.toFixed(1);
  }
  return shortValue + suffixes[suffixNum];
};

export const replaceZerosWithUndefinedDeep = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(replaceZerosWithUndefinedDeep);
  } else if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === 0 ? undefined : replaceZerosWithUndefinedDeep(value),
      ]),
    );
  } else {
    return obj;
  }
};

///// Filter Form Validation : compair Min Value with Max Value
export const minMaxValidation = (
  minValue: string | null,
  maxValue: string | null,
) => {
  if (
    maxValue !== "" &&
    minValue !== "" &&
    minValue !== null &&
    Number(maxValue) < Number(minValue)
  ) {
    return true;
  } else {
    return false;
  }
};

///// Filter Form Validation : compair Max Value with Min Value
export const maxMinValidation = (
  minValue: string | null,
  maxValue: string | null,
) => {
  if (
    minValue !== "" &&
    maxValue !== "" &&
    maxValue !== null &&
    Number(minValue) > Number(maxValue)
  ) {
    return true;
  } else {
    return false;
  }
};

export const username = () => {
  return getUserInfo("username");
};

export const getUserInfo = (key?: string): any => {
  const authUser = localStorage.getItem("authUser");

  if (!authUser) {
    return key ? "" : {};
  }

  try {
    const parsed = JSON.parse(authUser);

    // if key provided return value else whole object
    return key ? (parsed?.[key] ?? "") : parsed;
  } catch {
    return key ? "" : {};
  }
};

export const userTerminal = () => {
  return getUserInfo("userTerminal");
};

// Implement logic for display filter Criteria on top up the grid
export const filterCriteriaValue = (
  params: any,
  tab: number,
  hasFilterOsdType: boolean = false,
): string => {
  const criteria: string[] = [];

  const addCriterion = (condition: boolean, label: string) => {
    if (condition) criteria.push(label);
  };

  const isTabIn = (tabList: number[]) => tabList.includes(tab);

  addCriterion(params?.entryDateFrom || params?.entryDateTo, "Date");
  addCriterion(params?.origin?.length > 0, "Origin TID");
  addCriterion(
    params?.destination?.length > 0 &&
      isTabIn([ETab.overages, ETab.myAssignments, ETab.dashboard]),
    "Destination TID",
  );
  addCriterion(
    params?.reporting?.length > 0 &&
      isTabIn([
        ETab.shortages,
        ETab.damages,
        ETab.misdelivery,
        ETab.dashboard,
        ETab.onHandDelivery,
        ETab.myAssignments,
      ]),
    "Reporting TID",
  );
  addCriterion(
    !hasFilterOsdType && params?.osdType?.length > 0,
    "Exception Code",
  );
  addCriterion(params?.commodityType?.length > 0, "Commodity");
  addCriterion(Boolean(params?.description), "Description");
  addCriterion(params?.proNumber || params?.proNumber === 0, "Pro #");
  addCriterion(Boolean(params?.shipperName), "Shipper Name");
  addCriterion(Boolean(params?.consigneeName), "Consignee Name");
  addCriterion(Boolean(params?.od400ProNumber), "OD400 Pro #");
  addCriterion(Boolean(params?.osdNumber), "Exception #");
  addCriterion(params?.values?.length > 0, "Value Range");
  addCriterion(params?.status?.length > 0, "Status");
  addCriterion(Boolean(params?.entryBy), "Entry By");
  addCriterion(
    isTabIn([
      ETab.overages,
      ETab.dashboard,
      ETab.shortages,
      ETab.damages,
      ETab.onHandDelivery,
      ETab.misdelivery,
    ]) && Boolean(params?.assignedTo),
    "Assigned To",
  );
  addCriterion(
    isTabIn([
      ETab.myAssignments,
      ETab.shortages,
      ETab.damages,
      ETab.onHandDelivery,
      ETab.misdelivery,
    ]) && Boolean(params?.assignedBy),
    "Assigned By",
  );

  addCriterion(Boolean(params?.dockWorkerName), "Dock Worker Name");
  addCriterion(Boolean(params?.driverName), "Driver Name");

  return criteria.join(" / ");
};

export const noOptionsText = (
  values: string = "",
  loading?: boolean,
  fieldName?: EAutoSearchFieldName,
  minSize: number = 2,
) => {
  if (values?.length < minSize) {
    return `Please Enter ${fieldName}`;
  } else if (values?.length > 1 && !loading) {
    return "Records not found";
  } else if (loading) {
    return "Loading...";
  } else {
    return `No ${fieldName} found`;
  }
};

export const sortTerminals = (terminals: TTerminal[]) => {
  return terminals.sort((a, b) => {
    let regionA = a.region ?? a.terminalId;
    let regionB = b.region ?? b.terminalId;

    if (regionA === regionB) {
      return a.region === null ? -1 : 1;
    }
    if (regionA < regionB) {
      return -1;
    }
    if (regionA > regionB) {
      return 1;
    }
    return 0;
  });
};

// handle .,+,-, e, E for number type field
export const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if ([".", "-", "+", "e", "E"].includes(event.key)) {
    event.preventDefault();
  }
};

export const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
  // Get the pasted text
  const pastedText = event.clipboardData.getData("text");

  // Check if the pasted text is a valid number
  if (Number.isNaN(Number(pastedText))) {
    // If not a number, prevent the paste action
    event.preventDefault();
  }
};

export const handleTextFieldKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Enter",
  ];

  // Allow Ctrl + key combinations (e.g., Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  // Allow only digits and allowed control keys
  if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};
export const handleNumberMaxLength = (
  e: React.ChangeEvent<HTMLInputElement>,
  maxLength: number = 1,
) => {
  if (!e.target.value) return;
  e.target.value = Math.max(0, Number.parseInt(e.target.value))
    .toString()
    .slice(0, maxLength);
};

export const combineTowJsonObject = (objValue: any, srcValue: any) => {
  // Take the value from the left object if both values are present
  return _.isUndefined(srcValue) ? objValue : srcValue;
};

// Normalizes values by converting null, undefined, or empty strings to null
export const normalizeValues = <T extends object>(obj: T): T => {
  const normalize = (value: any): any => {
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return null;
    }
    if (Array.isArray(value)) {
      // Normalize each item in the array
      const normalizedArray = value.map(normalize);
      // Filter out objects that are only `undefined`
      const cleanedArray = normalizedArray.filter(
        (item) =>
          !(
            typeof item === "object" &&
            item !== null &&
            _.every(item, (v) => v === undefined)
          ),
      );
      return cleanedArray.length === 0 ? [] : cleanedArray;
    }
    if (typeof value === "object" && value !== null) {
      return _.mapValues(value, normalize);
    }
    return value;
  };

  return _.cloneDeepWith(obj, (value) => normalize(value)) as T;
};

/**
 * Compares two objects after normalizing their values.
 * @param obj1 First object.
 * @param obj2 Second object.
 * @returns True if the objects are deeply equal after normalization, false otherwise.
 */
export const compareObjects = <T extends object>(obj1: T, obj2: T): boolean => {
  const removeUndefinedObjectPropsDeep = (value: any): void => {
    if (Array.isArray(value)) {
      value.forEach(removeUndefinedObjectPropsDeep);
      return;
    }

    if (value && typeof value === "object") {
      Object.keys(value).forEach((key) => {
        if (value[key] === undefined) {
          delete value[key];
          return;
        }
        removeUndefinedObjectPropsDeep(value[key]);
      });
    }
  };

  const clonedObj1 = structuredClone(obj1);
  const clonedObj2 = structuredClone(obj2);
  removeUndefinedObjectPropsDeep(clonedObj1);
  removeUndefinedObjectPropsDeep(clonedObj2);

  const normalizedObj1 = normalizeValues(clonedObj1);
  const normalizedObj2 = normalizeValues(clonedObj2);
  return _.isEqual(normalizedObj1, normalizedObj2);
};

export const excludeKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
};

export const trimStrings = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(trimStrings); // Recursively handle arrays
  } else if (typeof obj === "object" && obj !== null) {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const value = obj[key];
        acc[key] =
          typeof value === "string" ? value.trim() : trimStrings(value); // Trim strings or process recursively
        return acc;
      },
      {} as Record<string, any>,
    );
  }
  return obj; // For non-objects (numbers, booleans, etc.), return as is
};

export const determineTab = (pathname: string): ETab => {
  const pathToTabMap: Record<string, ETab> = {
    [AppURLs.myAssignments]: ETab.myAssignments,
    [AppURLs.overages]: ETab.overages,
    [AppURLs.damages]: ETab.damages,
    [AppURLs.shortages]: ETab.shortages,
    [AppURLs.onHandDelivery]: ETab.onHandDelivery,
    [AppURLs.misdelivery]: ETab.misdelivery,
    [AppURLs.searchException]: ETab.dashboard,
  };

  return pathToTabMap[pathname] || ETab.dashboard;
};

export const chunkArray = <T>(
  array: T[] = [],
  maxLength: number = 1,
): T[][] => {
  if (array.length <= maxLength) {
    return [array];
  }

  const chunkCount = array.length <= 16 ? 2 : 3;
  const partSize = Math.ceil(array.length / chunkCount);

  return Array.from({ length: chunkCount }, (_, i) =>
    array.slice(i * partSize, (i + 1) * partSize),
  );
};

export function normalizeEmptyFields<T extends Record<string, any>>(
  obj: T | null | undefined,
  fields: (keyof T)[],
): Partial<T> | null | undefined {
  if (!obj) return obj;

  const normalized: Partial<T> = { ...obj };

  fields.forEach((field) => {
    const value = normalized[field];

    if (value === 0) {
      normalized[field] = undefined as any;
    }
  });

  return normalized;
}
